import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Text,
  Title,
  Button,
  Space,
  Modal,
  Input,
  Switch,
} from "react_reusable_components";
import { Row, Col } from "antd";

import { ThreeRow } from "../../core/layouts";
import Styles from "./SetCountryStatusModalContainer.module.scss";
import variables from "../../themes/base/styles/variables";
import ProductCategoryService from "../../services/product-category-service";
import { useIntl } from "react-intl";
import { getLocalizedMessage } from "../../UtilityFunctions/utils";
import { GlobalLoaderContext } from "../../globalContext/globalLoader/globalLoaderProvider";

const MarkStyle = {
  maskStyle: {
    backdropFilter: variables.highBlur,
    background: variables.blurBackground,
  },
  topSectionStyle: {
    borderBottom: `1px solid ${variables.grey4}`,
    padding: variables.paddingMedium,
    paddingBottom: variables.paddingSmall,
    display: variables.dFlex,
    alignItems: "start",
    justifyContent: "space-between",
  },
  modalHeader: {
    fontSize: variables.fontSizeXlarge,
    fontWeight: variables.fontWeightBold,
    lineHeight: variables.lineHeightXxlarge,
    marginBottom: 0,
  },
  modalHeaderDesc: {
    fontSize: variables.fontSizeNormal,
    fontWeight: variables.fontWeightNormal,
    lineHeight: variables.lineHeightNormal,
  },
  topSectionContainer: {
    display: variables.dFlex,
    justifyContent: variables.spaceBetween,
    alignItems: variables.alignCenter,
    padding: variables.paddingSmedium,
    paddingBottom: variables.paddingSmall,
  },
  closeIcon: {
    width: variables.iconWidthLarge,
    height: variables.iconHeightLarge,
  },
  unsetAll: { all: variables.unset, cursor: "pointer" },
  cancelBtn: {
    border: `1px solid ${variables.grey4}`,
    backgroundColor: variables.bodyBg,
    color: variables.textDefault,
    fontSize: variables.fontSizeNormal,
    fontWeight: variables.fontWeightNormal,
    lineHeight: variables.lineHeightNormal,
    height: variables.buttonHeightNormal,
  },
  changeBtn: {
    fontSize: variables.fontSizeNormal,
    fontWeight: variables.fontWeightNormal,
    lineHeight: variables.lineHeightNormal,
    height: variables.buttonHeightNormal,
  },
  bottomSectionStyle: {
    borderTop: `1px solid ${variables.grey4}`,
    display: variables.dFlex,
    justifyContent: "end",
    padding: variables.paddingMedium,
    gap: variables.gapMediumSmall,
  },
  middleSectionStyle: {
    padding: `0 ${variables.paddingMedium}`,
    maxHeight: variables.activeInactiveHeight,
    overflowY: "auto",
    width: variables.fullWidth,
    display: "block",
  },
  subCategoryListing: {
    width: variables.fullWidth,
    margin: `${variables.marginMedium} 0`,
  },
  subCategoryListingTitle: {
    fontWeight: variables.fontWeightBold,
    fontSize: variables.fontSizeNormal,
    color: variables.textDefault,
    textTransform: "capitalize",
  },
  inputStyle: {
    padding: `${variables.paddingSmallest} ${variables.paddingSmall2}"`,
    fontSize: variables.fontSizeNormal,
    fontWeight: variables.fontWeightNormal,
    height: variables.inputHeightSmall,
  },
  gridStyle: {
    display: "grid",
    gridTemplateColumns: `repeat(auto-fill, minmax(${variables.markActiveInputWidth}, 1fr))`,
    gridGap: variables.gapXS3,
    width: variables.fullWidth,
  },
};

export default function SetCountryStatusModalContainer({
  open,
  onClose,
  record,
}) {
  const intl = useIntl();
  const [productCategory, setProductCategory] = useState({
    productCategoryName: "",
    subCategory: [],
  });
  const [disabled, setDisabled] = useState(false);
  const loaderDispatch = useContext(GlobalLoaderContext)[1];
  let tempProductCategory = useRef(null);

  useEffect(() => {
    ProductCategoryService.getProductCategoryById(
      null,
      loaderDispatch,
      record.productCategoryId,
      intl
    )
      .then((data) => {
        setProductCategory(data);
        tempProductCategory.current = JSON.parse(JSON.stringify(data));
      })
      .catch(() => {}); //NOSONAR
  }, [record]);

  const handleToggleActive = (checked, subCategoryIndex, countryIndex) => {
    setProductCategory((prevState) => {
      const newState = { ...prevState };
      newState.subCategory[subCategoryIndex].country[countryIndex].isActive =
        checked;
      return newState;
    });
  };

  const mapSubCategories = (subCategory) =>
    subCategory
      ?.map(({ id: subCategoryId, country = [] }) =>
        country?.map(({ isActive, id: countryId }) => ({
          countryId,
          isActive,
          subCategoryId,
        }))
      )
      .flat()
      .filter(Boolean);

  const onSave = () => {
    setDisabled(true);
    const data = mapSubCategories(productCategory?.subCategory);
    const tempData = mapSubCategories(
      tempProductCategory?.current?.subCategory
    );

    const updatedData = data.map(({ isActive, ...rest }, index) => ({
      ...rest,
      isActive,
      isUpdated: isActive !== tempData[index].isActive,
    }));

    ProductCategoryService.markActiveInactive({ data: updatedData }, intl)
      .then(() => {
        setDisabled(false);
        onClose();
      })
      .catch(() => {
        setDisabled(false);
      });
  };

  const showSave = productCategory?.subCategory
    ?.map(({ country = [] }) => country?.length > 0)
    .includes(true);

  return (
    <Modal
      maskClosable={false}
      maskStyle={MarkStyle.maskStyle}
      centered
      open={open}
      closable={false}
      footer={null}
      width={673}
      disableGutter
    >
      <ThreeRow
        topSectionStyle={MarkStyle.topSectionStyle}
        topSection={
          <>
            <Space size={8} direction="vertical">
              <Title style={MarkStyle.modalHeader}>
                {getLocalizedMessage(
                  intl,
                  "productCategory.markActiveInactive"
                )}
              </Title>
              <Text style={MarkStyle.modalHeaderDesc}>
                {getLocalizedMessage(
                  intl,
                  "productCategory.markActiveInactiveDesc",
                  { name: productCategory?.productCategoryName }
                )}
              </Text>
            </Space>
            <Button
              type="text"
              style={MarkStyle.unsetAll}
              icon={
                <img
                  style={MarkStyle.closeIcon}
                  src={"/icons/close.svg"}
                  alt="close"
                />
              }
              onClick={onClose}
            />
          </>
        }
        middleSection={
          <div
            className={Styles["middle-section"]}
            style={MarkStyle.middleSectionStyle}
          >
            {productCategory?.subCategory?.map(
              ({ name, country }, productCategoryIdx) => (
                <Row key={name} style={MarkStyle.subCategoryListing}>
                  <Col xs={24} lg={24} className={Styles.mb1}>
                    <Text style={MarkStyle.subCategoryListingTitle}>
                      {name}
                    </Text>
                  </Col>
                  <div style={MarkStyle.gridStyle}>
                    {country && country?.length > 0 ? (
                      country?.map(
                        ({ id, isActive, name: countryName }, countryIdx) => (
                          <Input
                            style={MarkStyle.inputStyle}
                            subCategory
                            value={countryName}
                            key={id}
                            readOnly
                            suffix={
                              <Switch
                                onChange={(checked) => {
                                  handleToggleActive(
                                    checked,
                                    productCategoryIdx,
                                    countryIdx
                                  );
                                }}
                                size="medium"
                                checked={isActive}
                              />
                            }
                          />
                        )
                      )
                    ) : (
                      <Text style={MarkStyle.modalHeaderDesc}>
                        {getLocalizedMessage(
                          intl,
                          "productCategory.noCountryAssigned"
                        )}
                      </Text>
                    )}
                  </div>
                </Row>
              )
            )}
          </div>
        }
        bottomSection={
          <>
            <Button
              onClick={onClose}
              icon={null}
              style={MarkStyle.cancelBtn}
              disabled={disabled}
            >
              {getLocalizedMessage(intl, "button.text.cancel")}
            </Button>
            {showSave && (
              <Button
                onClick={onSave}
                style={MarkStyle.changeBtn}
                icon={null}
                disabled={disabled}
              >
                {getLocalizedMessage(intl, "button.text.save")}
              </Button>
            )}
          </>
        }
        bottomSectionStyle={MarkStyle.bottomSectionStyle}
      />
    </Modal>
  );
}
